/**
 * Merges duplicate contexts by a given key (e.g. 'text') and combines their
 * issues & stakeholders into the first occurrence of that context.
 *
 * @param contexts Array of context objects.
 * @param key Property name to determine uniqueness (default: 'text').
 * @returns A new array with unique contexts and merged nested data.
 */
export function mergeDuplicateContexts(contexts: any[], key: string = 'text'): any[] {
  if (!Array.isArray(contexts)) return [];

  const mergedMap = new Map<string, any>();

  // Helper to merge items uniquely by name
  const mergeUniqueByName = (items: any[] = []): any[] => {
    const uniqueMap = new Map<string, any>();
    items.forEach(item => {
      const name = item?.name?.trim();
      if (!name) return;
      uniqueMap.set(name, { ...(uniqueMap.get(name) || {}), ...item });
    });
    return Array.from(uniqueMap.values());
  };

  // Process each context
  contexts.forEach(ctx => {
    const uniqueKey = ctx?.[key]?.trim?.() || ctx?.[key];
    if (!uniqueKey) return;

    if (!mergedMap.has(uniqueKey)) {
      // First occurrence → store as-is
      mergedMap.set(uniqueKey, { ...ctx });
    } else {
      // Duplicate → merge its issues and stakeholders into the first one
      const existing = mergedMap.get(uniqueKey);

      existing.issues = mergeUniqueByName([
        ...(existing.issues || []),
        ...(ctx.issues || [])
      ]);

      existing.stakeholders = mergeUniqueByName([
        ...(existing.stakeholders || []),
        ...(ctx.stakeholders || [])
      ]);
    }
  });

  return Array.from(mergedMap.values()).map((ctx, index) => ({
    id: index + 1,
    ...ctx
  }));
}
